import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useQueries } from '@tanstack/react-query';
import { groupBy } from 'lodash';
import {
  IngredientDto,
  ProductCategoryDetailsDto,
  ProductDto,
  ProductSizeDto,
  UpcomingProductDto,
  UpcomingProductSizeDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { ProductPriceCalculationRequest, ProductSizePriceDto } from '@kotipizzagroup/kotipizza-pricing-api-client';
import {
  upcomingProductPricesCacheKey,
  useAllIngredients,
  useAllProductCategories,
  useAllProductMenus,
  useAllProductPrices,
  useAllProducts,
  useAllS4DIngredients,
  useAllS4DProducts,
  useUpcomingProducts,
} from '../../../apis/products-api';
import { calculateProductDefaultPrices } from '../../../apis/pricing-api';
import { compareProductToS4D, S4DProductComparison } from '../../ProductsPage/utils/s4dProductComparison';

const TEST_MENU_ID = 3;

export type OverviewSize = UpcomingProductSizeDto & {
  /** Undefined while the price is being calculated. */
  price?: number;
  pictureCount: number;
};

export type OverviewProduct = Omit<UpcomingProductDto, 'sizes'> & {
  sizes: OverviewSize[];
};

export type FullCardData = {
  product: OverviewProduct;
  comparison?: S4DProductComparison;
  isTestMenuOnly: boolean;
  /** In at least one active menu. */
  isInMenu: boolean;
  /** Has price rows, i.e. its sizes were chosen and saved on the product page. */
  hasSavedPrices: boolean;
  pricesFailed: boolean;
};

export type RemovedRecipeRow = {
  ingredientId: number;
  name: string;
  quantity: number;
  isActive: boolean;
};

export type RemovedCardData = {
  productId: number;
  name: string;
  categoryName: string;
  recipe: RemovedRecipeRow[];
};

export type OverviewGroup = {
  key: string;
  /** Null for the group of products that are only in the test menu. */
  date: dayjs.Dayjs | null;
  added: FullCardData[];
  removed: RemovedCardData[];
};

const countPictures = (size: UpcomingProductSizeDto): number =>
  [size.hasDesktopImage, size.hasMobileImage, size.hasDesktopMenuImage, size.hasMobileMenuImage].filter(Boolean).length;

// Same shape the upcoming-products endpoint builds, so one card renders both.
const toUpcomingProduct = (
  product: ProductDto,
  category: ProductCategoryDetailsDto | undefined,
  ingredientsById: Map<number, IngredientDto>
): UpcomingProductDto => ({
  productId: product.productId,
  name: product.name,
  categoryId: product.productCategoryId,
  categoryName: product.productCategoryName,
  releaseDateTime: product.startDate ?? undefined,
  endingDateTime: product.endDate,
  description: product.description,
  sizes: (category?.productSizes ?? []).map((size) => {
    const images = product.productImages?.filter((image) => image.productSizeId === size.productSizeId) ?? [];

    return {
      sizeId: size.productSizeId,
      name: size.name,
      hasDesktopImage: images.some((image) => image.imageUrl),
      hasMobileImage: images.some((image) => image.mobileImageUrl),
      hasDesktopMenuImage: images.some((image) => image.menuImageUrl),
      hasMobileMenuImage: images.some((image) => image.mobileMenuImageUrl),
      ingredients: (product.ingredients ?? []).map((row) => {
        const ingredient = ingredientsById.get(row.ingredientId);

        return {
          ingredientId: row.ingredientId,
          ingredientCategoryId: ingredient?.ingredientCategoryId,
          ingredientName: ingredient?.name,
          isActive: Boolean(ingredient?.isActive),
          quantity: row.quantity,
          dosing: ingredient?.dosings?.find((dosing) => dosing.productSizeId === size.productSizeId)?.dosingInGrams,
          energy: ingredient?.energy,
          carbonFootprint: ingredient?.carbonFootprint,
        };
      }),
    };
  }),
});

// Same request the product page builds. The fallback is for an upcoming product missing from the product list.
const toPriceRequest = (
  upcoming: UpcomingProductDto,
  product: ProductDto | undefined,
  productSizes: ProductSizeDto[],
  allIngredients: IngredientDto[]
): ProductPriceCalculationRequest =>
  product
    ? ({ product, ingredients: allIngredients, productSizes } as ProductPriceCalculationRequest)
    : toFallbackPriceRequest(upcoming, allIngredients);

const toFallbackPriceRequest = (
  product: UpcomingProductDto,
  allIngredients: IngredientDto[]
): ProductPriceCalculationRequest =>
  ({
    product: {
      hasFixedMinimumPrice: false,
      hasMinimumPrice: false,
      ingredients: product.sizes?.[0]?.ingredients?.map((ingredient) => ({
        productId: product.productId,
        ingredientId: ingredient.ingredientId,
        quantity: ingredient.quantity,
        maxQuantity: 3,
        minQuantity: 0,
      })),
    },
    ingredients: allIngredients,
    productSizes: product.sizes?.map((size) => ({
      productSizeId: size.sizeId,
      name: size.name,
      displayOrder: 0,
      productCategoryId: product.categoryId,
    })),
  } as ProductPriceCalculationRequest);

// The product page's rule: the minimum price when one is set, otherwise the recipe's calculated price.
const getSizePrice = (
  minimumPrice: number | undefined,
  calculatedPrice: number | undefined,
  isCalculated: boolean
): number | undefined => {
  if (minimumPrice && minimumPrice > 0) return minimumPrice;
  return isCalculated ? calculatedPrice ?? 0 : undefined;
};

const dayKey = (date: dayjs.Dayjs): string => date.format('YYYY-MM-DD');

export const useUpcomingProductOverview = () => {
  const upcomingQuery = useUpcomingProducts();
  const productsQuery = useAllProducts();
  const ingredientsQuery = useAllIngredients();
  const categoriesQuery = useAllProductCategories();
  const menusQuery = useAllProductMenus();
  const priceRowsQuery = useAllProductPrices();
  const s4dProductsQuery = useAllS4DProducts();
  const s4dIngredientsQuery = useAllS4DIngredients();
  const requiredQueries = [upcomingQuery, productsQuery, ingredientsQuery, categoriesQuery, menusQuery, priceRowsQuery];

  const { data: upcomingProducts } = upcomingQuery;
  const { data: products } = productsQuery;
  const { data: ingredients } = ingredientsQuery;
  const { data: categories } = categoriesQuery;
  const { data: menus } = menusQuery;
  const { data: priceRows } = priceRowsQuery;
  const { data: s4dProducts } = s4dProductsQuery;
  const { data: s4dIngredients } = s4dIngredientsQuery;

  const ingredientsById = useMemo(
    () => new Map((ingredients ?? []).map((ingredient) => [ingredient.ingredientId ?? 0, ingredient])),
    [ingredients]
  );

  const categoriesById = useMemo(
    () => new Map((categories ?? []).map((category) => [category.productCategoryId, category])),
    [categories]
  );

  const productsById = useMemo(
    () => new Map((products ?? []).map((product) => [product.productId ?? 0, product])),
    [products]
  );

  const priceRowsByProductId = useMemo(() => groupBy(priceRows ?? [], (row) => row.productId), [priceRows]);

  // An inactive menu reaches no restaurant, so it doesn't count.
  const activeMenus = useMemo(() => (menus ?? []).filter((menu) => menu.isActive), [menus]);
  const inMenuIds = useMemo(() => new Set(activeMenus.flatMap((menu) => menu.productIds ?? [])), [activeMenus]);

  const testMenuOnlyProducts = useMemo(() => {
    if (!products || !menus) return [];

    const testMenu = menus.find((menu) => menu.productMenuId === TEST_MENU_ID);
    if (!testMenu?.productIds) return [];

    const inOtherMenus = new Set(
      activeMenus.filter((menu) => menu.productMenuId !== TEST_MENU_ID).flatMap((menu) => menu.productIds ?? [])
    );
    const upcomingIds = new Set((upcomingProducts ?? []).map((product) => product.productId));
    const testMenuIds = new Set(testMenu.productIds);
    // `active` is a manual switch, so an active product past its end date is shown nowhere.
    const now = dayjs();

    return products
      .filter(
        (product) =>
          product.productId &&
          product.active &&
          !product.isArchived &&
          !(product.endDate && dayjs(product.endDate).isBefore(now)) &&
          testMenuIds.has(product.productId) &&
          !inOtherMenus.has(product.productId) &&
          !upcomingIds.has(product.productId)
      )
      .map((product) => toUpcomingProduct(product, categoriesById.get(product.productCategoryId), ingredientsById));
  }, [products, menus, activeMenus, upcomingProducts, categoriesById, ingredientsById]);

  // Like the product list, a product has the sizes it has price rows for. A brand-new product
  // may have none yet, so it falls back to every size in its category.
  const getProductSizes = useCallback(
    (productId: number, categoryId: number | null | undefined): ProductSizeDto[] => {
      const productPriceRows = priceRowsByProductId[productId] ?? [];
      const categorySizes = categoriesById.get(categoryId ?? 0)?.productSizes ?? [];
      if (productPriceRows.length === 0) return categorySizes;

      return categorySizes.filter((size) => productPriceRows.some((row) => row.productSizeId === size.productSizeId));
    },
    [priceRowsByProductId, categoriesById]
  );

  const pricedProducts = useMemo(
    () => [...(upcomingProducts ?? []), ...testMenuOnlyProducts],
    [upcomingProducts, testMenuOnlyProducts]
  );

  // The request is built from the product, its sizes and the ingredients, so wait for all of them.
  const canCalculatePrices = Boolean(ingredients && products && priceRows && categories);
  const priceQueries = useQueries({
    queries: pricedProducts.map((upcoming) => ({
      queryKey: [...upcomingProductPricesCacheKey, upcoming.productId],
      queryFn: () =>
        calculateProductDefaultPrices(
          toPriceRequest(
            upcoming,
            productsById.get(upcoming.productId ?? 0),
            getProductSizes(upcoming.productId ?? 0, upcoming.categoryId),
            ingredients ?? []
          )
        ),
      enabled: canCalculatePrices,
      refetchOnWindowFocus: false,
    })),
  });

  // useQueries returns a new array every render, so memoize on when the results last changed.
  const pricesUpdatedAt = priceQueries.map((query) => `${query.status}-${query.dataUpdatedAt}`).join();
  const { pricesByProductId, failedPriceIds } = useMemo(() => {
    const prices = new Map<number, ProductSizePriceDto[]>();
    const failed = new Set<number>();
    priceQueries.forEach((query, index) => {
      const productId = pricedProducts[index].productId ?? 0;
      if (query.data) prices.set(productId, query.data);
      if (query.isError) failed.add(productId);
    });
    return { pricesByProductId: prices, failedPriceIds: failed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricedProducts, pricesUpdatedAt]);

  const toFullCard = useCallback(
    (upcoming: UpcomingProductDto, isTestMenuOnly: boolean): FullCardData => {
      const productId = upcoming.productId ?? 0;
      const prices = pricesByProductId.get(productId);
      const productPriceRows = priceRowsByProductId[productId] ?? [];
      const productSizes = getProductSizes(productId, upcoming.categoryId);
      const sizeOrder = (sizeId?: number) =>
        productSizes.find((size) => size.productSizeId === sizeId)?.displayOrder ?? 0;

      const sizes: OverviewSize[] = (upcoming.sizes ?? [])
        .filter((size) => productSizes.some((productSize) => productSize.productSizeId === size.sizeId))
        .sort((first, second) => sizeOrder(first.sizeId) - sizeOrder(second.sizeId))
        .map((size) => ({
          ...size,
          pictureCount: countPictures(size),
          price: getSizePrice(
            productPriceRows.find((row) => row.productSizeId === size.sizeId)?.minimumPrice,
            prices?.find((price) => price.productSizeID === size.sizeId)?.calculatedPrice,
            Boolean(prices)
          ),
        }));

      const product = productsById.get(productId);
      const comparison =
        product && s4dProducts && s4dIngredients && ingredients
          ? compareProductToS4D({
              product,
              s4dProducts,
              s4dIngredients,
              ingredients,
              productSizes,
              ourPricesBySizeId: new Map(
                sizes.filter((size) => size.price).map((size) => [size.sizeId ?? 0, size.price as number])
              ),
            })
          : undefined;

      return {
        product: { ...upcoming, sizes },
        comparison,
        isTestMenuOnly,
        isInMenu: isTestMenuOnly || inMenuIds.has(productId),
        hasSavedPrices: productPriceRows.length > 0,
        pricesFailed: failedPriceIds.has(productId),
      };
    },
    [
      getProductSizes,
      pricesByProductId,
      failedPriceIds,
      priceRowsByProductId,
      productsById,
      inMenuIds,
      s4dProducts,
      s4dIngredients,
      ingredients,
    ]
  );

  const groups = useMemo(() => {
    const byDay = new Map<string, OverviewGroup>();
    const groupFor = (date: dayjs.Dayjs): OverviewGroup => {
      const key = dayKey(date);
      let group = byDay.get(key);
      if (!group) {
        group = { key, date: date.startOf('day'), added: [], removed: [] };
        byDay.set(key, group);
      }
      return group;
    };

    (upcomingProducts ?? []).forEach((upcoming) => {
      if (!upcoming.releaseDateTime) return;
      groupFor(dayjs(upcoming.releaseDateTime)).added.push(toFullCard(upcoming, false));
    });

    const now = dayjs();
    (products ?? []).forEach((product) => {
      if (!product.productId || product.isArchived || !product.active || !product.endDate) return;
      const endDate = dayjs(product.endDate);
      if (endDate.isBefore(now)) return;

      groupFor(endDate).removed.push({
        productId: product.productId,
        name: product.name,
        categoryName: product.productCategoryName,
        recipe: (product.ingredients ?? []).map((row) => {
          const ingredient = ingredientsById.get(row.ingredientId);
          return {
            ingredientId: row.ingredientId,
            name: ingredient?.name ?? String(row.ingredientId),
            quantity: row.quantity,
            isActive: Boolean(ingredient?.isActive),
          };
        }),
      });
    });

    const sorted = [...byDay.values()].sort((first, second) => first.key.localeCompare(second.key));

    if (testMenuOnlyProducts.length > 0) {
      sorted.push({
        key: 'test-menu',
        date: null,
        added: testMenuOnlyProducts.map((product) => toFullCard(product, true)),
        removed: [],
      });
    }

    return sorted;
  }, [upcomingProducts, products, testMenuOnlyProducts, ingredientsById, toFullCard]);

  const getIngredientName = (ingredientId: number) => ingredientsById.get(ingredientId)?.name;

  return {
    isLoading: requiredQueries.some((query) => query.isLoading),
    isError: requiredQueries.some((query) => query.isError),
    isS4DError: s4dProductsQuery.isError || s4dIngredientsQuery.isError,
    groups,
    getIngredientName,
  };
};
